import { StopOutlined } from "@ant-design/icons";
import { AutoComplete, Button, Checkbox, Col, Form, Input, Modal, Row, Select } from "antd";
import { CardCategories, MaxTitleLength } from "models/cardIngestion";
import { useCardTitleContext } from "pages/typer/CardTitleContext";
import { FC, useEffect, useState } from "react";
import { toSelectOptions } from "util/helpers";

interface ListingForm {
    cardId: string;
    cardTitle: string;
    isLot: boolean;
    category: string;
    subCategory: string;
    statusId: string;
    conditionId: string | null;
    grader: string | null;
    grade: string | null;
}

interface EditListingModalProps {
    cardId: string;
    sku: string;
    onSaved?: (title: string) => Promise<void>;
    onDoNotUpload?: (cardId: string) => Promise<void>;
}

const EditListingModal: FC<EditListingModalProps> = ({
    cardId,
    sku,
    onSaved,
    onDoNotUpload
}) => {
    const [open, setOpen] = useState<boolean>(false);
    const [touched, setTouched] = useState<boolean>(false);
    const [form] = Form.useForm<ListingForm>();

    const {
        cardTitle,
        isLot,
        category,
        subCategory,
        statusId,
        conditionId,
        auctionCategory,
        gradingCompany,
        grader,
        grade,
        isGraded,
        working,
        availableSubCategories,
        loadCardTitle,
        saveCardTitle,
        setTitle,
        setIsLot,
        setCategory,
        setSubcategory,
        setStatus,
        setGradingCompany,
        setCondition,
        doNotUploadCard,
        reset,
        setUseNavigation,
    } = useCardTitleContext();

    useEffect(() => {
        if (open) {
            setUseNavigation(false);
            loadCardTitle(cardId);
        }
    }, [open, loadCardTitle, setUseNavigation, cardId]);

    useEffect(() => {
        if (open) {
            form.setFieldsValue({
                cardId,
                cardTitle,
                isLot,
                category,
                subCategory,
                statusId,
                conditionId,
                grader,
                grade
            });
        }
    }, [
        form,
        open,
        cardId,
        cardTitle,
        isLot,
        category,
        subCategory,
        statusId,
        conditionId,
        grader,
        grade
    ]);

    const submit = async () => {
        await form.validateFields();

        await saveCardTitle();
        if (onSaved) {
            onSaved(cardTitle);
        }

        setOpen(false);
        setTouched(false);
    }

    const cancel = async () => {
        setOpen(false);

        if (touched) {
            await reset();
            setTouched(false);
        }
    }

    const doNotUpload = async () => {
        await doNotUploadCard('Do Not Upload');

        if (onDoNotUpload) {
            await onDoNotUpload(cardId);
        }

        setOpen(false);
    }

    const footer = <Row>
        <Col span={5}>
            <Button
                danger
                type="primary"
                icon={<StopOutlined />}
                onClick={doNotUpload}
                disabled={working}
            >
                Do Not Upload
            </Button>
        </Col>
        <Col span={6} offset={13}>
            <Button
                onClick={cancel}
                disabled={working}
            >
                Cancel
            </Button>
            <Button
                type="primary"
                onClick={submit}
                loading={working}
            >
                OK
            </Button>
        </Col>
    </Row>;

    return <>
        <Button type="link" onClick={() => setOpen(true)}>Edit Listing</Button>
        <Modal
            title={`Edit Listing - ${sku}`}
            open={open}
            width='700px'
            keyboard={false}
            maskClosable={false}
            footer={footer}
        >
            <Form
                form={form}
                preserve={false}
                layout="vertical"
                disabled={working}
                onValuesChange={() => setTouched(true)}
            >
                <Row gutter={[32, 0]}>
                    <Col span={24}>
                        <Form.Item
                            label="Card Title"
                            name="cardTitle"
                            rules={[
                                { required: true, message: 'Card title is required' },
                                { max: MaxTitleLength, message: `Card title has to be ${MaxTitleLength} characters or less` }
                            ]}>
                            <Input
                                showCount={{ formatter: (args) => `${args.count} / ${MaxTitleLength}` }}
                                onChange={e => setTitle(e.target.value)}
                                spellCheck
                            />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            label="Category"
                            name="category"
                            rules={[
                                { required: true }
                            ]}
                        >
                            <Select options={CardCategories} onChange={setCategory} />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            label="Sub Category"
                            name="subCategory"
                            rules={[
                                { required: true, message: 'You have to enter a sub category' }
                            ]}
                        >
                            <AutoComplete
                                filterOption
                                defaultOpen={false}
                                onChange={setSubcategory}
                                options={availableSubCategories.map(value => ({ value }))}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={5}>
                        <Form.Item
                            label=" "
                            valuePropName="checked"
                            name="isLot"
                        >
                            <Checkbox onChange={e => setIsLot(e.target.checked)}>Lot Listing</Checkbox>
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            label="Status"
                            name="statusId"
                            rules={[
                                { required: true }
                            ]}
                        >
                            <Select
                                options={toSelectOptions(auctionCategory.itemConditions)}
                                onChange={v => setStatus(v)}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12} hidden={!isGraded}>
                        <Form.Item
                            label="Grader"
                            name="grader"
                            rules={[
                                { required: isGraded, message: 'You have to select a grader' }
                            ]}
                        >
                            <Select
                                options={toSelectOptions(auctionCategory.graders, true)}
                                onChange={v => setGradingCompany(v)}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={6} hidden={!isGraded}>
                        <Form.Item
                            label="Grade"
                            name="grade"
                            rules={[
                                { required: isGraded, message: 'You have to select a grade' }
                            ]}
                        >
                            <Select disabled={!gradingCompany}
                                options={toSelectOptions(gradingCompany?.grades, true)}
                                onChange={v => setGradingCompany(grader, v)}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={8} hidden={isGraded || isLot}>
                        <Form.Item
                            label="Condition"
                            name="conditionId"
                            rules={[
                                { required: !isGraded && !isLot }
                            ]}
                        >
                            <Select
                                options={toSelectOptions(auctionCategory.ungradedConditions)}
                                onChange={setCondition}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    </>
};

export default EditListingModal;